import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import logo from '../../assets/images/logo.png';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Alert, TextInput } from 'flowbite-react';
import { HiExclamation} from 'react-icons/hi';
import { RootState, useAppDispatch, useAppSelector } from '../../redux';
import { getAuthMethods, authenticate, getOpenIdUrl } from '../../redux';
import type { Credentials } from '../../services';
import { Button, Icon } from '../../components';
import { Config } from '../../config';
import './Login.css';

function Login() {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const { authMethods, isLoggingIn, logInErrorMessage } = useAppSelector((state : RootState) => state.auth);
    const [ssoError, setSsoError] = useState<string | null>(null);
    const [isSsoRedirecting, setIsSsoRedirecting] = useState(false);

    useEffect(() => {
        dispatch(getAuthMethods());
    }, [dispatch]);

    useEffect(() => {
        const code = searchParams.get('sso_error');
        if (!code)
            return;
        setSsoError(t(`login.sso_error.${code}`, { defaultValue: t('login.sso_error.unknown') }) as string);
        const next = new URLSearchParams(searchParams);
        next.delete('sso_error');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, t]);

    const submit = function (values: Credentials) : void {
        dispatch(authenticate(values));
    }

    const getSsoRedirectUrl = () => {
        if (isSsoRedirecting)
            return;
        // Keep loading until the browser leaves the page; clearing on API
        // success made the button look idle during the IdP navigation.
        setIsSsoRedirecting(true);
        dispatch(getOpenIdUrl((err: Error | null, url?: string) => {
            if (url) {
                window.location.assign(url);
                return;
            }
            setIsSsoRedirecting(false);
        }));
    }

    const emailFieldComponent = (props: AnyObj) => (
        <TextInput
            type="email"
            autoComplete="off"
            icon={() => <Icon type="email" />}
            {...props}
            theme={{ "field": { "input": { "withAddon": { "off": "rounded-md" }}}}}
            className="Login-input border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 sm:text-sm rounded-md focus:ring-cyan-600 focus:border-cyan-600"
        />
    );

    const passwordFieldComponent = (props: AnyObj) => (
        <TextInput
            type="password"
            autoComplete="off"
            icon={() => <Icon type="lock" />}
            {...props}
            theme={{ "field": { "input": { "withAddon": { "off": "rounded-md" }}}}}
            className="Login-input border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 sm:text-sm rounded-md focus:ring-cyan-600 focus:border-cyan-600"
        />
    );

    document.title = `Yoda - ${t('login.sign_in')}`;
    const errorMessage = logInErrorMessage || ssoError;

    return (
        <div className="Login flex min-h-screen items-center justify-center px-6 py-12">
            <div className="Login-shell mx-auto w-full max-w-md">
                <header className="Login-brand mb-10 text-center">
                    <div className="flex items-center justify-center gap-4">
                        <img src={ logo } className="h-14 w-14" alt="Yoda" />
                        <span className="font-netox text-5xl font-bold tracking-tight text-white">
                            Yoda
                        </span>
                    </div>
                    <p className="mx-auto mt-5 max-w-sm text-base leading-relaxed text-gray-400">
                        { t('login.tagline') }
                    </p>
                </header>

                <div className="Login-form-surface rounded-xl px-6 py-8 sm:px-8">
                    <h1 className="text-xl font-semibold text-white">
                        { t('login.sign_in') }
                    </h1>
                    <p className="mt-1.5 text-sm text-gray-400">
                        { t('login.welcome') }
                    </p>

                    { errorMessage && (
                        <Alert color="failure" icon={HiExclamation} className="mt-6">
                            <span className="font-medium">
                                {errorMessage}
                            </span>
                        </Alert>
                    )}

                    {authMethods?.password && (
                    <Formik
                        initialValues={{ email: '', password: '' }}
                        validate={values => {
                            if (!values.email) {
                                return { email: t('login.email_is_required') };
                            }
                            if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
                                return { email: t('login.email_is_invalid') };
                            }
                            return {};
                        }}
                        onSubmit={(values) => {
                            submit(values);
                        }}
                    >
                         {() => (
                             <Form className="mt-7 space-y-5">
                                <div className="Login-field">
                                    <label className="mb-2 block text-sm font-medium text-gray-300">
                                        { t('login.email') }
                                    </label>
                                    <Field as={emailFieldComponent} name="email" required />
                                    <ErrorMessage name="email" component="div" className="mt-1 text-sm text-red-400" />
                                </div>
                                <div className="Login-field">
                                    <label className="mb-2 block text-sm font-medium text-gray-300">
                                        { t('login.password') }
                                    </label>
                                    <Field as={passwordFieldComponent} name="password" required />
                                    <ErrorMessage name="password" component="div" className="mt-1 text-sm text-red-400" />
                                </div>
                                <Button
                                    className="Login-cta w-full"
                                    icon="user"
                                    type="submit"
                                    color="secondary"
                                    loading={isLoggingIn}
                                    title={t('login.sign_in')}
                                />
                            </Form>
                        )}
                    </Formik>
                    )}

                    {authMethods?.openid && (
                        <div className={authMethods.password ? 'mt-6' : 'mt-7'}>
                            {authMethods.password && (
                                <div className="mb-4 flex h-fit items-center justify-center space-x-3">
                                    <hr className="mb-0 flex-1 border-white/10" />
                                    <span className="text-sm font-light text-gray-500 dark:text-gray-400">
                                        {t('login.or_login_with')}
                                    </span>
                                    <hr className="mb-0 flex-1 border-white/10" />
                                </div>
                            )}
                            <Button
                                className="Login-cta Login-cta-sso w-full"
                                icon="sso"
                                color="primary"
                                loading={isSsoRedirecting}
                                title={isSsoRedirecting ? t('login.sso_redirecting') : t('login.sso')}
                                onClick={getSsoRedirectUrl}
                            />
                        </div>
                    )}
                </div>

                <p className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
                    v.{Config.app_version}
                </p>
            </div>
        </div>
    );
}

export default Login;
